// RolesPage.jsx — الأدوار والصلاحيات (بالتصميم الموحّد)
import React, { useState, useEffect, useMemo, useCallback, forwardRef } from 'react';
import rolesApi from '../../api/rolesApi';
import { useAuth } from '../../context/AuthContext';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Tooltip, Chip, Dialog, Slide, Checkbox,
  InputAdornment, LinearProgress, CircularProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';
import { ReportPage, fieldSx, BRAND } from '../../components/Reports/ReportKit';

const BORDER = '#e5e7eb';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ───────────────────────── أسماء عربية للصلاحيات ─────────────────────────
const ACTIONS = {
  Create: 'إنشاء', Add: 'إضافة', Edit: 'تعديل', Update: 'تحديث', Delete: 'حذف', Remove: 'إزالة',
  View: 'عرض', Read: 'قراءة', List: 'عرض القائمة', Access: 'دخول', Print: 'طباعة', Approve: 'اعتماد',
  Unapprove: 'إلغاء الاعتماد', Reject: 'رفض', Export: 'تصدير', Import: 'استيراد', Manage: 'إدارة',
  Post: 'ترحيل', Unpost: 'إلغاء الترحيل', Cancel: 'إلغاء', Return: 'مرتجع', Close: 'إقفال', Open: 'فتح',
  Transfer: 'تحويل', Move: 'نقل', Sync: 'مزامنة', Send: 'إرسال', Discount: 'خصم', Price: 'تعديل السعر',
};
const CATEGORIES = {
  Invoice: 'الفواتير', Customer: 'العملاء', Vendor: 'الموردين', Supplier: 'الموردين', Bank: 'البنوك',
  Safe: 'الصناديق', Bond: 'السندات', Voucher: 'السندات', Product: 'المنتجات', Category: 'التصنيفات',
  Unit: 'الوحدات', Stock: 'المخزون', Warehouse: 'المستودعات', Branch: 'الفروع', Company: 'المنشأة',
  User: 'المستخدمين', Role: 'الأدوار', Security: 'الأمان', Report: 'التقارير', Reports: 'التقارير',
  Setting: 'الإعدادات', Settings: 'الإعدادات', GISettings: 'الإعدادات العامة', ChartOfAccounts: 'شجرة الحسابات',
  JournalEntry: 'القيود اليومية', CostCenter: 'مراكز التكلفة', Pos: 'نقاط البيع', POS: 'نقاط البيع',
  Zatca: 'الفوترة الإلكترونية', Dashboard: 'لوحة التحكم', Salesman: 'المندوبين', Tax: 'الضرائب',
  'Invoice.Sales': 'فواتير المبيعات', 'Invoice.Purchase': 'فواتير المشتريات', 'Invoice.Quotation': 'عروض الأسعار',
  'Invoice.PurchaseReturn': 'مرتجع المشتريات', 'Invoice.SalesReturn': 'مرتجع المبيعات', 'Invoice.OpeningStock': 'رصيد افتتاحي',
  'Invoice.StockIn': 'إدخال مخزني', 'Invoice.StockOut': 'إخراج مخزني', 'Invoice.Transfer': 'مناقلة',
  'Bond.Receipt': 'سندات القبض', 'Bond.Payment': 'سندات الصرف', 'Bond.Tax': 'سند ضريبة',
};
const actionName = (perm) => { const a = perm.split('.').pop(); return ACTIONS[a] || a; };
const categoryOf = (perm) => {
  const p = perm.split('.');
  return (p[0] === 'Invoice' || p[0] === 'Bond') && p.length > 2 ? `${p[0]}.${p[1]}` : p[0];
};
const categoryName = (c) => CATEGORIES[c] || c;

// ======================================================================
export default function RolesPage() {
  const { hasPermission } = useAuth();
  const currentUserName = localStorage.getItem('userName') || '';
  const isAuthorized = currentUserName.toLowerCase() === 'owner' || hasPermission('Role.Manage') || hasPermission('Security.Manage');
  if (!isAuthorized) return <NotAuthorized />;
  return <RolesManager />;
}

function NotAuthorized() {
  return (
    <Box dir="rtl" sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', p: 3 }}>
      <Paper elevation={0} sx={{ p: 4, maxWidth: 440, textAlign: 'center', borderRadius: '18px', border: `1px solid ${BORDER}` }}>
        <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 2, borderRadius: '20px', display: 'grid', placeItems: 'center', bgcolor: BRAND.primarySoft, color: BRAND.primary }}>
          <LockOutlinedIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>ليس لديك صلاحية للوصول</Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14, lineHeight: 1.8 }}>
          هذه الصفحة مخصصة للمدير العام (Owner) أو لمن يملك صلاحية إدارة الأدوار.
        </Typography>
      </Paper>
    </Box>
  );
}

// ======================================================================
function RolesManager() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState(null); // { id, name, description, permissions, readOnly }

  const fetchRoles = useCallback(async () => {
    try {
      const res = await rolesApi.getAll(companyId);
      setRoles(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('فشل تحميل الأدوار');
    }
  }, [companyId]);

  useEffect(() => {
    Promise.all([
      fetchRoles(),
      rolesApi.getPermissions().then(r => setPermissions(Array.isArray(r.data) ? r.data : [])).catch(console.error),
    ]).finally(() => setLoading(false));
  }, [fetchRoles]);

  const sync = async () => {
    setSyncing(true);
    try {
      await rolesApi.syncPermissions();
      toast.success('تم تحديث صلاحيات الأدوار النظامية');
      await fetchRoles();
      const r = await rolesApi.getPermissions().catch(() => null);
      if (Array.isArray(r?.data)) setPermissions(r.data);
    } catch {
      toast.error('فشل تحديث الصلاحيات');
    } finally {
      setSyncing(false);
    }
  };

  const remove = async (role) => {
    if (!window.confirm(`هل أنت متأكد من حذف الدور "${role.name}"؟`)) return;
    try {
      await rolesApi.delete(role.id);
      toast.success('تم حذف الدور');
      fetchRoles();
    } catch {
      toast.error('لا يمكن حذف هذا الدور (قد يكون مرتبطًا بمستخدمين)');
    }
  };

  const openNew = () => setEditor({ id: null, name: '', description: '', permissions: [] });
  const openEdit = (r) => setEditor({ id: r.id, name: r.name, description: r.description || '', permissions: [...(r.permissions || [])], readOnly: !!r.isSystemRole });
  const openCopy = (r) => setEditor({ id: null, name: `${r.name} (نسخة)`, description: r.description || '', permissions: [...(r.permissions || [])] });

  const total = permissions.length;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? roles.filter(r => `${r.name} ${r.description || ''}`.toLowerCase().includes(q)) : roles;
  }, [roles, search]);

  return (
    <ReportPage
      title="الأدوار والصلاحيات"
      subtitle="حدد ما يستطيع كل دور عرضه وتنفيذه في النظام"
      icon={<AdminPanelSettingsOutlinedIcon />}
      actions={[
        { label: syncing ? 'جاري التحديث...' : 'تحديث صلاحيات النظام', icon: syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />, onClick: sync, disabled: syncing },
        { label: 'دور جديد', icon: <AddIcon />, onClick: openNew, primary: true },
      ]}
      stats={[
        { label: 'عدد الأدوار', value: roles.length, icon: <GroupsOutlinedIcon />, color: 'primary' },
        { label: 'أدوار النظام', value: roles.filter(r => r.isSystemRole).length, icon: <GppGoodOutlinedIcon />, color: 'info' },
        { label: 'أدوار مخصصة', value: roles.filter(r => !r.isSystemRole).length, icon: <AdminPanelSettingsOutlinedIcon />, color: 'success' },
        { label: 'إجمالي الصلاحيات', value: total, icon: <KeyOutlinedIcon />, color: 'warning' },
      ]}
    >
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.25, mb: 1.5 }}>
        <TextField size="small" fullWidth placeholder="بحث باسم الدور أو الوصف..." value={search} onChange={e => setSearch(e.target.value)} sx={fieldSx}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ border: `1px dashed #cbd5e1`, borderRadius: '14px', py: 6, textAlign: 'center', color: '#64748b' }}>
          <GroupsOutlinedIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
          <Typography sx={{ fontWeight: 700, mt: 1 }}>{search ? 'لا توجد نتائج' : 'لا توجد أدوار مضافة'}</Typography>
          {!search && <Button onClick={openNew} startIcon={<AddIcon />} sx={{ mt: 1.5, fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إضافة أول دور</Button>}
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' } }}>
          {filtered.map(r => {
            const count = (r.permissions || []).length;
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <Paper key={r.id} elevation={0} sx={{
                border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25,
                transition: '.15s', '&:hover': { borderColor: BRAND.primaryLight, boxShadow: '0 8px 22px -14px rgba(79,70,229,.45)' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '11px', flexShrink: 0, display: 'grid', placeItems: 'center',
                    bgcolor: r.isSystemRole ? '#f1f5f9' : BRAND.primarySoft, color: r.isSystemRole ? '#475569' : BRAND.primary }}>
                    {r.isSystemRole ? <GppGoodOutlinedIcon /> : <AdminPanelSettingsOutlinedIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .75 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 15 }} noWrap>{r.name}</Typography>
                      {r.isSystemRole && <Chip label="نظام" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: '#64748b', minHeight: 20 }} noWrap>{r.description || '—'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{count} صلاحية</Typography>
                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{pct}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 6, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: BRAND.primary } }} />
                </Box>
                <Box sx={{ display: 'flex', gap: .5, mt: 'auto', pt: .5, borderTop: '1px dashed #eef0f4' }}>
                  {r.isSystemRole ? (
                    <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => openEdit(r)} sx={{ fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>عرض</Button>
                  ) : (
                    <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => openEdit(r)} sx={{ fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>تعديل</Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="نسخ كدور جديد"><IconButton size="small" onClick={() => openCopy(r)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  {!r.isSystemRole && (
                    <Tooltip title="حذف"><IconButton size="small" onClick={() => remove(r)} sx={{ color: '#e11d48' }}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {editor && (
        <RoleEditor
          initial={editor}
          allPermissions={permissions}
          companyId={companyId}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); fetchRoles(); }}
          onAddPermission={(p) => setPermissions(prev => (prev.includes(p) ? prev : [...prev, p]))}
        />
      )}
    </ReportPage>
  );
}

// ======================================================================
function RoleEditor({ initial, allPermissions, companyId, onClose, onSaved, onAddPermission }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState('all'); // all | on | off
  const [newPerm, setNewPerm] = useState('');
  const ro = !!initial.readOnly;
  const selected = useMemo(() => new Set(form.permissions), [form.permissions]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = {};
    allPermissions.forEach(p => {
      const c = categoryOf(p);
      const hay = `${p} ${actionName(p)} ${categoryName(c)}`.toLowerCase();
      if (q && !hay.includes(q)) return;
      if (show === 'on' && !selected.has(p)) return;
      if (show === 'off' && selected.has(p)) return;
      (map[c] = map[c] || []).push(p);
    });
    return Object.entries(map).sort((a, b) => categoryName(a[0]).localeCompare(categoryName(b[0]), 'ar'));
  }, [allPermissions, query, show, selected]);

  const setPerms = (fn) => setForm(f => ({ ...f, permissions: fn(new Set(f.permissions)) }));
  const toggle = (p) => !ro && setPerms(s => { if (s.has(p)) s.delete(p); else s.add(p); return [...s]; });
  const setGroup = (perms, on) => !ro && setPerms(s => { perms.forEach(p => (on ? s.add(p) : s.delete(p))); return [...s]; });

  const addCustom = () => {
    const p = newPerm.trim();
    if (!p) return;
    onAddPermission(p);
    setPerms(s => { s.add(p); return [...s]; });
    setNewPerm('');
    toast.success(`تمت إضافة الصلاحية: ${p}`);
  };

  const save = useCallback(async () => {
    if (ro) return;
    if (!form.name.trim()) { toast.warning('يرجى إدخال اسم الدور'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, permissions: form.permissions, companyId };
      if (form.id) await rolesApi.update(form.id, payload);
      else await rolesApi.create(payload);
      toast.success(form.id ? 'تم تحديث الدور' : 'تم إنشاء الدور');
      onSaved();
    } catch {
      toast.error('حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  }, [form, companyId, ro, onSaved]);

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (!saving) save(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, saving]);

  const total = allPermissions.length;
  const count = form.permissions.length;

  return (
    <Dialog open fullScreen onClose={onClose} TransitionComponent={Transition} dir="rtl" PaperProps={{ sx: { bgcolor: '#f1f5f9' } }}>
      {/* الرأس */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1.5, sm: 3 }, py: 1.5, color: '#fff', background: BRAND.headerGradient, boxShadow: BRAND.headerShadow }}>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }} noWrap>
            {ro ? `عرض الدور: ${form.name}` : form.id ? `تعديل الدور: ${form.name || ''}` : 'دور جديد'}
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: .85 }}>{count} من {total} صلاحية {ro ? '— دور نظامي للعرض فقط' : ''}</Typography>
        </Box>
        {!ro && (
          <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            sx={{ bgcolor: '#fff', color: BRAND.primaryDark, fontWeight: 800, borderRadius: '11px', px: 2.5, '&:hover': { bgcolor: BRAND.primarySoft }, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        )}
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0,1fr)' }, alignItems: 'start' }}>
        {/* بيانات الدور */}
        <Box sx={{ display: 'grid', gap: 1.5, position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 2, display: 'grid', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>بيانات الدور</Typography>
            <TextField size="small" label="اسم الدور *" value={form.name} disabled={ro} sx={fieldSx}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus={!form.id} />
            <TextField size="small" label="الوصف" value={form.description} disabled={ro} multiline minRows={2} sx={fieldSx}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>الصلاحيات الممنوحة</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: BRAND.primary, direction: 'ltr' }}>{count} / {total}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={total ? (count / total) * 100 : 0}
                sx={{ height: 8, borderRadius: 8, bgcolor: '#eef2ff', '& .MuiLinearProgress-bar': { borderRadius: 8, bgcolor: BRAND.primary } }} />
            </Box>
            {!ro && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth variant="outlined" size="small" startIcon={<DoneAllIcon />} onClick={() => setForm(f => ({ ...f, permissions: [...allPermissions] }))}
                  sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>منح الكل</Button>
                <Button fullWidth variant="outlined" color="error" size="small" startIcon={<RemoveDoneIcon />} onClick={() => setForm(f => ({ ...f, permissions: [] }))}
                  sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إلغاء الكل</Button>
              </Box>
            )}
          </Paper>

          {!ro && (
            <Paper elevation={0} sx={{ border: '1px dashed #cbd5e1', borderRadius: '14px', p: 2, display: 'grid', gap: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>إضافة صلاحية يدويًا</Typography>
              <TextField size="small" placeholder="مثال: Invoice.Type.Special" value={newPerm} sx={fieldSx}
                onChange={e => setNewPerm(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
                inputProps={{ dir: 'ltr' }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addCustom} disabled={!newPerm.trim()}
                sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: BRAND.primary, '& .MuiButton-startIcon': { ml: .5, mr: -.25 } }}>إضافة</Button>
            </Paper>
          )}
        </Box>

        {/* الصلاحيات */}
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', p: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="بحث في الصلاحيات (مثال: اعتماد، فواتير، Invoice)..." value={query} onChange={e => setQuery(e.target.value)}
              sx={{ ...fieldSx, flex: 1, minWidth: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)}
              sx={{ '& .MuiToggleButton-root': { px: 1.5, fontWeight: 700, borderRadius: '10px !important', border: `1px solid ${BORDER} !important`, mx: .25 },
                '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important` } }}>
              <ToggleButton value="all">الكل</ToggleButton>
              <ToggleButton value="on">الممنوحة</ToggleButton>
              <ToggleButton value="off">غير الممنوحة</ToggleButton>
            </ToggleButtonGroup>
          </Paper>

          {groups.length === 0 ? (
            <Paper elevation={0} sx={{ border: '1px dashed #cbd5e1', borderRadius: '14px', py: 5, textAlign: 'center', color: '#64748b' }}>لا توجد صلاحيات مطابقة</Paper>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, alignItems: 'start' }}>
              {groups.map(([cat, perms]) => {
                const on = perms.filter(p => selected.has(p)).length;
                const all = on === perms.length, some = on > 0 && !all;
                return (
                  <Paper key={cat} elevation={0} sx={{ border: `1px solid ${all ? '#c7d2fe' : BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
                    <Box onClick={() => setGroup(perms, !all)}
                      sx={{ display: 'flex', alignItems: 'center', gap: .5, px: 1, py: .5, bgcolor: all ? BRAND.primarySoft : '#fafbfc', borderBottom: `1px solid ${BORDER}`, cursor: ro ? 'default' : 'pointer' }}>
                      <Checkbox size="small" checked={all} indeterminate={some} disabled={ro} sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: BRAND.primary } }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 14, flex: 1 }}>{categoryName(cat)}</Typography>
                      <Chip size="small" label={`${on}/${perms.length}`} sx={{ height: 22, fontWeight: 800, fontSize: 11.5, bgcolor: on ? BRAND.primary : '#e2e8f0', color: on ? '#fff' : '#475569' }} />
                    </Box>
                    <Box sx={{ p: .5 }}>
                      {perms.map(p => (
                        <Box key={p} onClick={() => toggle(p)}
                          sx={{ display: 'flex', alignItems: 'center', gap: .5, px: .5, borderRadius: '8px', cursor: ro ? 'default' : 'pointer', '&:hover': { bgcolor: ro ? 'transparent' : '#f8fafc' } }}>
                          <Checkbox size="small" checked={selected.has(p)} disabled={ro} sx={{ '&.Mui-checked': { color: BRAND.primary } }} />
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, color: selected.has(p) ? '#0f172a' : '#64748b' }}>{actionName(p)}</Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8', direction: 'ltr' }}>{p}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}